import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { pipe } from 'fp-ts/function';
import { config } from '../../config';
import { useAppSelector } from '../../configuration/setup/hooks';
import type { RootState } from '../../configuration/setup/store';
import type { EulaStatusUpdateRequest } from '../app/types.ts';
import { Intention } from '../components/tabs/accounts/dialogs/AccountLifeCycleChange';
import {
    type Account,
    type AccountRest,
    type LifeCycleStateInformation,
    type ManEulaStatus,
    decodeAccount,
    decodeAccountsApiResponse,
    decodeManEulaApiResponse,
    validateLatamSpecificData,
    SearchIdentifier,
} from './schemas/accountsBackendCodec';
import { prepareHeaders, responseHandler } from './utils';

const TagO = {
    ACCOUNT: 'Account',
    EULA_STATUS: 'EulaStatus',
} as const;

export type Tag = (typeof TagO)[keyof typeof TagO];

const toExternalIdentifiers = (account: Account) => {
    const identifiers = account.remainingExternalIdentifiers ? [...account.remainingExternalIdentifiers] : [];
    for (const duns of account.dunsNumbers) {
        if (duns.length > 0) {
            identifiers.push({
                value: duns,
                type: 'DUNS',
            });
        }
    }

    return identifiers;
};

const mapEmptyStringToUndefined = (data: string | undefined) => (data ? data : undefined);

export const mapToRestPutResource = (account: Account) => {
    if (account.tenantSpecificData !== undefined) {
        validateLatamSpecificData(account.tenantSpecificData);
    }
    return {
        id: account.id,
        name: account.name,
        legal_address: {
            line_1: account.legalAddress.line1,
            line_2: account.legalAddress.line2,
            line_3: account.legalAddress.line3,
            city: account.legalAddress.city,
            postal_code: account.legalAddress.postCode,
            country_code: account.legalAddress.countryCode,
        },
        tax_id:
            // biome-ignore lint/complexity/useOptionalChain: suppressed out of convenience for migration to biomejs
            account.taxId && account.taxId.value && account.taxId.taxType
                ? {
                      value: account.taxId.value,
                      tax_type: account.taxId.taxType,
                  }
                : undefined,
        external_identifiers: toExternalIdentifiers(account),
        tenant: account.tenant,
        registration_channel: account.registrationChannel,
        account_type: account.accountType,
        tenant_specific_data: account.tenantSpecificData
            ? {
                  city_id: account.tenantSpecificData.cityId,
                  neighbourhood: account.tenantSpecificData.neighbourhood,
                  phone_number: account.tenantSpecificData.phoneNumber,
                  state: account.tenantSpecificData.state,
                  address_details: mapEmptyStringToUndefined(account.tenantSpecificData.addressDetails),
              }
            : undefined,
        contacts: account.contacts
            ? // only include general or billing if an email was provided
              // this is necessary to avoid objects such as {billing: {}} if no billing email is provided
              Object.fromEntries(
                  Object.entries(account.contacts)
                      .filter(([_, contact]) => !(contact?.email === undefined || contact?.email === ''))
                      .map(([key, contact]) => [key, { email: contact.email }])
              )
            : undefined,
        logistics_tenant: account.logisticsTenant ? account.logisticsTenant : undefined,
        language: account.language,
    };
};

export const mapToPostResource = (lifeCycleStateInformation: LifeCycleStateInformation) => ({
    locked_because: lifeCycleStateInformation.lockedBecause,
    locked_notes: lifeCycleStateInformation.lockedNotes,
});

const mapToEulaRestPostResource = (req: EulaStatusUpdateRequest, eulaStatus: ManEulaStatus) => ({
    status: eulaStatus.status,
    channel: 'RIO_SUPPORT',
    eula_version: eulaStatus.eula_version,
    legal_principal: {
        first_name: req.firstname,
        last_name: req.lastname,
        contact_info: req.email,
    },
    vin_snapshot: [],
});

const constructSearchPart = (search: string, searchIdentifier: SearchIdentifier): string => {
    const encodedSearch = encodeURIComponent(search);
    return searchIdentifier === SearchIdentifier.NAME
        ? `q=${encodedSearch}`
        : `external_identifier_value=${encodedSearch}&external_identifier_type=${searchIdentifier}`;
};

export const accountsApi = createApi({
    reducerPath: 'accountsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.ACCOUNTS_SERVICE,
        prepareHeaders,
    }),
    tagTypes: [TagO.ACCOUNT, TagO.EULA_STATUS],
    endpoints: build => ({
        // Queries
        getAccountById: build.query<AccountRest, string>({
            query: accountId => ({
                url: `/${accountId}?embed=(life_cycle_state_information)`,
                responseHandler,
            }),
            transformResponse: rawResult => decodeAccount(rawResult),
        }),
        getAccountNameById: build.query<string, string>({
            query: accountId => ({
                url: `/${accountId}`,
                responseHandler,
            }),
            transformResponse: rawResult => pipe(rawResult, decodeAccount, account => account.name),
        }),
        getAccountsByIds: build.query<AccountRest[], string[]>({
            query: accountIds => ({
                url: `?q=ids:${accountIds.join(',')}`,
                responseHandler,
            }),
            transformResponse: (rawResult: unknown) => pipe(rawResult, decodeAccountsApiResponse, it => it.items),
        }),
        getAllAccounts: build.query<AccountRest[], void>({
            query: () => ({
                url: '?embed=(life_cycle_state_information)',
                responseHandler,
            }),
            transformResponse: (rawResult: unknown) => pipe(rawResult, decodeAccountsApiResponse, it => it.items),
            providesTags: [TagO.ACCOUNT],
        }),
        getAccountsForSearchParam: build.query<AccountRest[], { search: string; searchIdentifier: SearchIdentifier }>({
            query: ({ search, searchIdentifier }) => ({
                url: `?${constructSearchPart(search, searchIdentifier)}&embed=(life_cycle_state_information)`,
                responseHandler,
            }),
            transformResponse: (rawResult: unknown) => pipe(rawResult, decodeAccountsApiResponse, it => it.items),
            providesTags: [TagO.ACCOUNT],
        }),

        getManEulaStatus: build.query<ManEulaStatus, { accountId: string }>({
            query: ({ accountId }) => ({
                url: `/${accountId}/man-eula-status`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return {
                        ...data,
                        etag: response.headers.get('ETag') || '',
                    };
                },
            }),
            transformResponse: (rawResult: unknown) => pipe(rawResult, decodeManEulaApiResponse),
            providesTags: [TagO.EULA_STATUS],
        }),

        // Mutations
        updateManEulaStatus: build.mutation<
            void,
            {
                req: EulaStatusUpdateRequest;
                eulaStatus: ManEulaStatus;
            }
        >({
            query: ({ req, eulaStatus }) => ({
                url: `/${eulaStatus.account_id}/man-eula-status`,
                method: 'POST',
                body: mapToEulaRestPostResource(req, eulaStatus),
                headers: {
                    'x-rio-audit-reason': req.reason,
                    'If-Match': eulaStatus.etag,
                },
                responseHandler,
            }),
            invalidatesTags: [TagO.EULA_STATUS],
        }),

        deleteAccount: build.mutation<void, { accountId: string; reason: string }>({
            query: ({ accountId, reason }) => ({
                url: `/${accountId}`,
                method: 'DELETE',
                headers: { 'x-rio-audit-reason': reason },
                responseHandler,
            }),
            invalidatesTags: [TagO.ACCOUNT],
        }),
        updateAccount: build.mutation<void, { account: Account; reason: string }>({
            query: ({ account, reason }) => {
                return {
                    url: `/${account.id}`,
                    method: 'PUT',
                    body: mapToRestPutResource(account),
                    headers: { 'x-rio-audit-reason': reason },
                    responseHandler,
                };
            },
            invalidatesTags: [TagO.ACCOUNT],
        }),
        changeLifeCycleState: build.mutation<
            void,
            {
                accountId: string;
                intention: Intention;
                reason: string;
                lifeCycleStateInformation?: LifeCycleStateInformation;
            }
        >({
            query: ({ accountId, intention, reason, lifeCycleStateInformation }) => ({
                url: `/${accountId}/${intention}`,
                method: 'POST',
                // biome-ignore lint/style/noNonNullAssertion: suppressed out of convenience for migration to biomejs
                body: intention === Intention.lock ? mapToPostResource(lifeCycleStateInformation!) : undefined,
                headers: { 'x-rio-audit-reason': reason },
                responseHandler,
            }),
            invalidatesTags: [TagO.ACCOUNT],
        }),
    }),
});

export const {
    useLazyGetAccountByIdQuery,
    useLazyGetAllAccountsQuery,
    useDeleteAccountMutation,
    useChangeLifeCycleStateMutation,
    useUpdateAccountMutation,
    useLazyGetAccountNameByIdQuery,
    useLazyGetAccountsByIdsQuery,
    useLazyGetAccountsForSearchParamQuery,
    useGetManEulaStatusQuery,
    useUpdateManEulaStatusMutation,
} = accountsApi;

// export const selectRelevantAssets = (state: RootState): Asset[] | undefined =>
//     assetsApi.endpoints.fetchAllRelevantAssets.select()(state).data;
// export const useRelevantAssets = () => useAppSelector(selectRelevantAssets);
export const selectEulaStatus = (state: RootState, accountId: string): ManEulaStatus | undefined =>
    accountsApi.endpoints.getManEulaStatus.select({ accountId: accountId })(state).data;
export const useEulaStatus = (accountId: string) => useAppSelector(state => selectEulaStatus(state, accountId));
